import { useEffect } from 'react';
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
// import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '@components/layout';
import { getSession } from '@/services/authService';
import { getStringBundle } from '@/services/localizationService';
import { useAuthStore } from '@/stores/authStore';
import { useLocalizationStore } from '@/stores/localizationStore';
import { useAuthBroadcast, broadcastLoggedIn } from '@/hooks/useAuthBroadcast';
import { useTranslation } from '@hooks/useTranslation';
import { AppDsProvider } from '@lib/dsProvider';

// Ensure staticData type augmentation is loaded
import '@lib/routerTypes';

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const { t } = useTranslation();
  const matches = useRouterState({ select: (s) => s.matches });
  const showShell = !matches.some(
    (m) => (m.staticData as Record<string, unknown> | undefined)?.hideShell,
  );

  const setSession = useAuthStore((s) => s.setSession);
  const session = useAuthStore((s) => s.session);
  const language = useLocalizationStore((s) => s.language);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['session'],
    queryFn: getSession,
    retry: false,
  });

  const setLanguage = useLocalizationStore((s) => s.setLanguage);

  // Listen for logout/session-expired/logged-in from other tabs
  useAuthBroadcast();

  useEffect(() => {
    if (data && !session) {
      setSession(data);
      // Notify other tabs that a session is now active
      broadcastLoggedIn();

      // Initialize locale from session if localStorage has no preference
      const storedLang = localStorage.getItem('wp_language');
      if (!storedLang && data.user.locale) {
        setLanguage(data.user.locale);
      }
    }
  }, [data, session, setSession, setLanguage]);

  // Prefetch the string bundle once session is available
  const organizationId = session?.organization.id ?? undefined;
  useQuery({
    queryKey: ['localization', 'bundle', language, organizationId ?? ''],
    queryFn: () => getStringBundle(language, organizationId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    enabled: !!session,
  });

  // Loading state while fetching session
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p>{t('K_LOADING', 'Loading...')}</p>
      </div>
    );
  }

  // Session fetch failed — the envelope interceptor's handleSessionExpiry()
  // has already redirected to /login. Render nothing while the browser navigates.
  if (isError) {
    return null;
  }

  if (!showShell) {
    return (
      <AppDsProvider>
        <Outlet />
        {/* {import.meta.env.DEV && location.hostname === 'localhost' && <TanStackRouterDevtools position="bottom-right" />} */}
      </AppDsProvider>
    );
  }

  return (
    <AppDsProvider>
      <AppShell>
        <Outlet />
      </AppShell>
      {/* {import.meta.env.DEV && location.hostname === 'localhost' && <TanStackRouterDevtools position="bottom-right" />} */}
    </AppDsProvider>
  );
}
